// src/pages/agent/Leads.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, limit, orderBy, onSnapshot, addDoc, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, Btn, Field, Input, Select, TextArea, BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, BORDER, MID, LIGHT, StatusPill, EmptyState } from '../../components/agent/UI';
import AddressAutocomplete from '../../components/agent/AddressAutocomplete';
import { lookupZip } from '../../lib/zipLookup';
import { checkOptOut, OptOutWarningModal } from '../../lib/optOutCheck';
import { useIsMobile } from '../../lib/useIsMobile';
import { INTRO_SMS_OPTIONS, interpolateIntro } from '../../lib/smsTemplates';

const STATUSES = ['New', 'Contacted', 'Negotiating', 'Under Contract', 'Assigned', 'Closed', 'Dead'];

export default function Leads() {
  const navigate = useNavigate();
  const { profile } = useAgentAuth();
  const isMobile = useIsMobile();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNew, setShowNew] = useState(searchParams.get('new') === '1');

  // Resolve which pipeline to subscribe to. Admin: their own. Contract
  // agent: the inviting admin's pipeline (so they can see the open queue
  // + their claimed leads via Firestore rules).
  useEffect(() => {
    if (!profile?.uid) return;
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      let path = profile.uid;
      if (profile.role === 'agent') {
        path = profile.invitedByUid || null;
        if (!path) {
          try {
            const aSnap = await getDocs(query(
              collection(db, 'agents'),
              where('role', '==', 'admin'),
              where('status', '==', 'active'),
              limit(1)
            ));
            if (!aSnap.empty) path = aSnap.docs[0].id;
          } catch {}
        }
        if (!path) path = profile.uid;
      }
      if (cancelled) return;
      const q = query(collection(db, 'agents', path, 'leads'), orderBy('updatedAt', 'desc'));
      unsub = onSnapshot(q, snap => {
        setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }, err => { console.error('Leads list read denied:', err.message); setLoading(false); });
    })();
    return () => { cancelled = true; unsub(); };
  }, [profile?.uid, profile?.role, profile?.invitedByUid]);

  const filtered = leads
    .filter(l => filter === 'All' || (l.status || '').toLowerCase() === filter.toLowerCase())
    .filter(l => {
      if (!search) return true;
      const hay = `${l.address?.line1 || ''} ${(l.owners || []).map(o => o.name).join(' ')} ${(l.owners || []).map(o => o.phone).join(' ')}`.toLowerCase();
      return hay.includes(search.toLowerCase());
    });

  const closeNew = useCallback(() => {
    setShowNew(false);
    if (searchParams.get('new')) { searchParams.delete('new'); setSearchParams(searchParams); }
  }, [searchParams, setSearchParams]);

  return (
    <>
      <PageHeader
        eyebrow="Pipeline"
        title="Leads"
        sub="Properties you're sourcing. Click a lead for the calculator, contracts, notes, and SMS thread."
        right={<Btn variant="gold" onClick={() => setShowNew(true)}>+ New Lead</Btn>}
      />

      <div style={s.toolbar}>
        <Input
          placeholder="Search address, owner, phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 360 }}
        />
      </div>

      <div style={{ ...s.toolbar, marginTop: 0 }}>
        <div style={s.filterRow}>
          {['All', ...STATUSES].map(st => (
            <button
              key={st}
              onClick={() => setFilter(st)}
              style={{ ...s.filterBtn, ...(filter === st ? s.filterBtnActive : {}) }}
            >{st}</button>
          ))}
        </div>
      </div>

      <Card padded={false}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: LIGHT }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={leads.length === 0 ? 'No leads yet' : 'No leads match'}
            sub={leads.length === 0 ? 'Add your first property — address, owner, phone. Everything else auto-fills.' : 'Try another filter or search.'}
            action={leads.length === 0 ? <Btn variant="gold" onClick={() => setShowNew(true)}>+ Add a lead</Btn> : null}
          />
        ) : isMobile ? (
          <ul style={mc.list}>
            {filtered.map(l => (
              <li key={l.id} style={mc.card} onClick={() => navigate(`/agent/leads/${l.id}`)}>
                <div style={mc.row1}>
                  <div style={mc.address}>
                    {l.address?.line1 || '(no address)'}
                  </div>
                  <StatusPill status={l.status} />
                </div>
                <div style={mc.city}>
                  {[l.address?.city, l.address?.state, l.address?.zip].filter(Boolean).join(', ') || '—'}
                </div>
                <div style={mc.row2}>
                  <div style={mc.owner}>{(l.owners || []).map(o => o.name).filter(Boolean).join(', ') || 'Property Owner'}</div>
                  <div style={mc.phone}>{(l.owners || [])[0]?.phone || '—'}</div>
                </div>
                <div style={mc.metaRow}>
                  {l.source === 'inbound_sms' && (
                    <span style={mc.fromSms}>📨 From SMS</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: LIGHT }}>{relativeTime(l.updatedAt) || '—'}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Property</th>
                <th style={s.th}>Owner</th>
                <th style={s.th}>Phone</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Last touch</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => (
                <tr key={l.id} style={s.tr} onClick={() => navigate(`/agent/leads/${l.id}`)}>
                  <td style={s.td}>
                    <div style={{ fontWeight: 500, color: BURG_DK, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>{l.address?.line1 || '(no address)'}</span>
                      {l.source === 'inbound_sms' && (
                        <span title="Auto-created from inbound SMS — needs review" style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: BURG, background: 'rgba(201,168,76,0.18)', border: '1px solid rgba(201,168,76,0.4)', padding: '2px 8px', borderRadius: 999 }}>
                          📨 From SMS
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: LIGHT }}>
                      {[l.address?.city, l.address?.state, l.address?.zip].filter(Boolean).join(', ')}
                    </div>
                  </td>
                  <td style={s.td}>{(l.owners || []).map(o => o.name).filter(Boolean).join(', ') || '—'}</td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{(l.owners || [])[0]?.phone || '—'}</td>
                  <td style={s.td}><StatusPill status={l.status} /></td>
                  <td style={{ ...s.td, fontSize: 11, color: LIGHT }}>{relativeTime(l.updatedAt) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showNew && <NewLeadModal onClose={closeNew} agentUid={profile?.uid} profile={profile} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// New Lead modal — mode picker → manual or parse → review with accordions
// ─────────────────────────────────────────────────────────────
function NewLeadModal({ onClose, agentUid, profile }) {
  const navigate = useNavigate();

  // step: 'mode' | 'manual' | 'parse-input' | 'review'
  const [step, setStep] = useState('mode');
  const [data, setData] = useState(blankLeadData());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [savedFlash, setSavedFlash] = useState('');

  // For Parse mode
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);

  // Once saved (either by manual Save or auto-save on Parse), holds the lead doc id.
  // Subsequent saves update the existing doc instead of creating a new one.
  const [savedLeadId, setSavedLeadId] = useState(null);

  // Duplicate-detection state
  const [dupes, setDupes] = useState([]);
  const [optOut, setOptOut] = useState(null);
  const [optOutOverridden, setOptOutOverridden] = useState(false);
  const [proceedAnyway, setProceedAnyway] = useState(false);

  const dirty = step !== 'mode' && hasContent(data);

  // ── validation, write, save handlers ────────────────────────
  const validate = () => {
    if (!data.owners[0]?.name?.trim()) return 'Owner name is required.';
    if (!data.owners[0]?.phone?.trim()) return 'Owner phone is required.';
    if (!data.listingUrl?.trim())       return 'Listing URL is required.';
    if (!data.address?.line1?.trim())   return 'Property address is required.';
    return '';
  };
  // Write or update the lead doc. Optionally bake in parseSource (URL+text) so
  // we can re-parse later from the lead detail page.
  const writeLead = async ({ extra = {} } = {}) => {
    const v = validate();
    if (v) { setErr(v); return null; }
    setErr('');

    // Opt-out gate — refuse to add a seller who texted STOP unless
    // admin has explicitly overridden in the modal. Same number works
    // for primary phone + altPhones.
    if (!savedLeadId && !optOutOverridden) {
      const allPhones = (data.owners || []).flatMap(o => [o.phone, ...(o.altPhones || [])]).filter(Boolean);
      for (const p of allPhones) {
        const opt = await checkOptOut(p);
        if (opt) { setOptOut(opt); return null; }
      }
    }

    // Duplicate check (skipped if user has already chosen to proceed anyway, or if
    // we're updating an existing saved doc).
    if (!savedLeadId && !proceedAnyway) {
      const phone = data.owners?.[0]?.phone || '';
      const found = await findDuplicateLeads(agentUid, {
        phone,
        line1: data.address?.line1,
        zip: data.address?.zip,
        listingUrl: data.listingUrl
      });
      if (found.length > 0) {
        setDupes(found);
        return null;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...buildLeadDoc(data),
        ...extra,
        updatedAt: serverTimestamp()
      };
      if (savedLeadId) {
        await updateDoc(doc(db, 'agents', agentUid, 'leads', savedLeadId), payload);
        return { id: savedLeadId };
      } else {
        const ref = await addDoc(collection(db, 'agents', agentUid, 'leads'), {
          ...payload,
          status: 'New',
          notes: [],
          contracts: [],
          smsThread: [],
          calc: null,
          createdAt: serverTimestamp(),
          createdBy: agentUid
        });
        setSavedLeadId(ref.id);
        return ref;
      }
    } catch (e) {
      setErr(e.message || 'Failed to save lead.');
      return null;
    } finally {
      setSaving(false);
    }
  };
  // Intro-SMS confirm prompt. We ALWAYS ask now (no silent auto-fire): the
  // agent picks a template, edits the message, verifies the city & state are
  // included, then sends. introSmsRequest holds the pending lead plus the
  // action to run once Send/Skip resolves (close, navigate, reset, etc.).
  const [introSmsRequest, setIntroSmsRequest] = useState(null);

  // The single intro-SMS send path. Routes through sendLeadSms and records
  // _introSmsSent so no other flow re-fires the intro — this is what prevents
  // the seller getting two texts.
  const sendIntroSms = async (leadId, phone, body) => {
    try {
      const fn = httpsCallable(functions, 'sendLeadSms');
      await fn({ leadId, to: phone, body, agentName: agentFirstName(profile) });
      await updateDoc(doc(db, 'agents', agentUid, 'leads', leadId), {
        status: 'Contacted',
        _introSmsSent: true,
        updatedAt: serverTimestamp()
      });
      setData(d => ({ ...d, _introSmsSent: true }));
      return `📤 Intro SMS sent to ${phone} from ${agentFirstName(profile)}.`;
    } catch (e) {
      return `⚠️ Lead saved, but intro SMS failed: ${e.message}`;
    }
  };

  // Open the intro-SMS confirm popup for a freshly-saved lead, then run
  // postAction once the agent sends or skips. If the lead already had its
  // intro sent (or has no phone), skip straight to postAction.
  const requestIntroThen = (leadRef, leadData, postAction) => {
    const phone = leadData.owners?.[0]?.phone;
    if (!phone || leadData._introSmsSent) { postAction(); return; }
    setIntroSmsRequest({
      leadId: leadRef.id,
      phone,
      city: leadData.address?.city || '',
      state: leadData.address?.state || '',
      assetType: leadData.assetType === 'land' ? 'land' : 'property',
      ownerName: leadData.owners?.[0]?.name || 'the seller',
      postAction
    });
  };

  // Resolve the active intro request: send (or skip), flash a status, then
  // run the queued postAction. Shared by every creation path.
  const handleIntroSend = async (finalBody) => {
    const req = introSmsRequest;
    setIntroSmsRequest(null);
    if (!req) return;
    const msg = await sendIntroSms(req.leadId, req.phone, finalBody);
    setSavedFlash(msg);
    setTimeout(() => setSavedFlash(''), 4000);
    req.postAction();
  };
  const handleIntroSkip = () => {
    const req = introSmsRequest;
    setIntroSmsRequest(null);
    if (!req) return;
    setSavedFlash('✓ Lead saved. Intro SMS skipped — you can send it later from the lead\'s SMS panel.');
    setTimeout(() => setSavedFlash(''), 5000);
    req.postAction();
  };

  const handleSaveAndClose = async (e) => {
    e?.preventDefault?.();
    const r = await writeLead();
    if (!r) return;
    requestIntroThen(r, data, () => onClose());
  };
  const handleSaveAndAddAnother = async () => {
    const r = await writeLead();
    if (!r) return;
    requestIntroThen(r, data, () => {
      setData(blankLeadData());
      setPasteText('');
      setStep('mode');
      setSavedLeadId(null);
      setProceedAnyway(false);
    });
  };
  const handleOpenProfile = async () => {
    const r = await writeLead();
    if (!r) return;
    requestIntroThen(r, data, () => {
      onClose();
      navigate(`/agent/leads/${r.id}`);
    });
  };
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const handleClose = () => {
    if (dirty) { setShowDiscardConfirm(true); return; }
    onClose();
  };
  const confirmDiscard = () => { setShowDiscardConfirm(false); onClose(); };

  // ── Parse listing ──────────────────────────────────────────────
  // Calls the Cloud Function, merges the result, runs duplicate check,
  // and auto-saves on first parse so the lead is persisted immediately.
  const runParse = async () => {
    setErr('');
    if (!data.listingUrl?.trim()) { setErr('Please paste the listing URL first.'); return; }
    if (!pasteText.trim() || pasteText.trim().length < 20) {
      setErr('Paste a chunk of listing details (description, facts table, address — the more, the better).');
      return;
    }
    setParsing(true);
    try {
      const fn = httpsCallable(functions, 'parseListingText');
      const res = await fn({ url: data.listingUrl.trim(), text: pasteText });
      if (!res.data?.ok) throw new Error(res.data?.message || 'Parsing failed.');
      const merged = mergeParsedIntoData(data, res.data.parsed);

      // Duplicate check before auto-save
      if (!proceedAnyway) {
        const phone = merged.owners?.[0]?.phone || '';
        const found = await findDuplicateLeads(agentUid, {
          phone,
          line1: merged.address?.line1,
          zip: merged.address?.zip,
          listingUrl: merged.listingUrl
        });
        if (found.length > 0) {
          setData(merged);
          setStep('review');
          setDupes(found);
          return;
        }
      }

      // No dupes → auto-save with parseSource
      setData(merged);
      setStep('review');
      setSaving(true);
      try {
        const ref = await addDoc(collection(db, 'agents', agentUid, 'leads'), {
          ...buildLeadDoc(merged),
          status: 'New',
          notes: [], contracts: [], smsThread: [], calc: null,
          parseSource: { url: data.listingUrl.trim(), text: pasteText, parsedAt: serverTimestamp() },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: agentUid
        });
        setSavedLeadId(ref.id);
        setSavedFlash('✓ Parsed and saved. Edit anything that\'s off, then click Done.');
        setTimeout(() => setSavedFlash(''), 5000);
        // Ask whether to send the intro SMS (always confirm — never silent).
        requestIntroThen(ref, merged, () => {});
      } finally {
        setSaving(false);
      }
    } catch (e) {
      setErr(`Couldn't parse: ${e.message}. You can switch to Manual entry instead.`);
    } finally {
      setParsing(false);
    }
  };

  // Re-parse the saved text (used when the AI got it wrong the first time).
  const reparse = async () => {
    if (!savedLeadId) { setErr('Save the lead first before re-parsing.'); return; }
    if (!data.listingUrl || !pasteText) { setErr('Need both URL and pasted text to re-parse.'); return; }
    setParsing(true); setErr('');
    try {
      const fn = httpsCallable(functions, 'parseListingText');
      const res = await fn({ url: data.listingUrl.trim(), text: pasteText });
      if (!res.data?.ok) throw new Error(res.data?.message || 'Parsing failed.');
      const merged = mergeParsedIntoData(blankLeadData(), res.data.parsed);
      setData(merged);
      await updateDoc(doc(db, 'agents', agentUid, 'leads', savedLeadId), {
        ...buildLeadDoc(merged),
        parseSource: { url: data.listingUrl.trim(), text: pasteText, parsedAt: serverTimestamp() },
        updatedAt: serverTimestamp()
      });
      setSavedFlash('✓ Re-parsed and saved.');
      setTimeout(() => setSavedFlash(''), 3000);
    } catch (e) {
      setErr(`Re-parse failed: ${e.message}`);
    } finally {
      setParsing(false);
    }
  };

  // ── Renders by step ─────────────────────────────────────────
  return (
    <div style={m.overlay} onClick={handleClose}>
      <div style={m.modal} onClick={e => e.stopPropagation()}>
        <div style={m.head}>
          <div>
            <div style={m.eyebrow}>{step === 'review' ? 'Review parsed lead' : 'New lead'}</div>
            <h2 style={m.title}>
              {step === 'mode'        && <>How would you like to add this <em style={{ fontStyle: 'italic', color: BURG }}>lead</em>?</>}
              {step === 'manual'      && <>Add a <em style={{ fontStyle: 'italic', color: BURG }}>property</em>.</>}
              {step === 'parse-input' && <>Parse a <em style={{ fontStyle: 'italic', color: BURG }}>listing</em>.</>}
              {step === 'review'      && <>Confirm everything <em style={{ fontStyle: 'italic', color: BURG }}>looks right</em>.</>}
            </h2>
          </div>
          <button onClick={handleClose} style={m.x}>×</button>
        </div>

        <div style={{ padding: '4px 24px 24px' }}>
          {step === 'mode' && (
            <div style={m.modeGrid}>
              <button type="button" onClick={() => setStep('phone')} style={{ ...m.modeCard, ...m.modeCardGold }}>
                <div style={m.modeIcon}>📞</div>
                <div style={m.modeName}>Start with a phone</div>
                <div style={m.modeDesc}>Just the seller's number. We'll text them your intro SMS, then drop you on the lead's page to fill the rest.</div>
              </button>
              <button type="button" onClick={() => setStep('manual')} style={m.modeCard}>
                <div style={m.modeIcon}>✎</div>
                <div style={m.modeName}>Manual entry</div>
                <div style={m.modeDesc}>Owner name, phone, listing URL, address. Quick-add a lead and fill the rest later.</div>
              </button>
              <button type="button" onClick={() => setStep('parse-input')} style={m.modeCard}>
                <div style={m.modeIcon}>✨</div>
                <div style={m.modeName}>Parse listing</div>
                <div style={m.modeDesc}>Paste a Zillow / Redfin / MLS URL plus the listing text. AI fills in everything.</div>
              </button>
            </div>
          )}

          {step === 'phone' && (
            <PhoneFirstStep
              onCancel={() => setStep('mode')}
              onSubmit={async ({ phone }) => {
                setErr('');
                // Build the minimal lead doc.
                const minimal = {
                  ...blankLeadData(),
                  owners: [{ name: '', phone, email: '', altPhones: [] }]
                };
                setData(minimal);
                setSaving(true);
                try {
                  const ref = await addDoc(collection(db, 'agents', agentUid, 'leads'), {
                    ...buildLeadDoc(minimal),
                    status: 'New',
                    notes: [],
                    contracts: [],
                    smsThread: [],
                    calc: null,
                    createdAt: serverTimestamp(),
                    createdBy: agentUid
                  });
                  // Always ask before texting: open the intro-SMS confirm
                  // (pick a template, edit, verify city & state), then drop
                  // the agent on the lead detail page either way.
                  requestIntroThen(ref, minimal, () => {
                    onClose();
                    navigate(`/agent/leads/${ref.id}`);
                  });
                } catch (e) {
                  setErr(`Couldn't create lead: ${e.message}`);
                } finally {
                  setSaving(false);
                }
              }}
              saving={saving}
            />
          )}

          {step === 'manual' && (
            <ManualForm data={data} setData={setData} />
          )}

          {step === 'parse-input' && (
            <>
              <Field label="Listing website URL" required hint="Zillow, Redfin, Realtor.com, MLS — anywhere this property is listed.">
                <Input
                  autoFocus
                  type="url"
                  value={data.listingUrl}
                  onChange={e => setData(d => ({ ...d, listingUrl: e.target.value }))}
                  placeholder="https://www.zillow.com/homedetails/..."
                />
              </Field>
              <Field label="Paste listing details" required hint="Select all (Cmd/Ctrl+A) on the listing page, copy, paste here. Description, facts table, owner phone — anything helps.">
                <div style={{ position: 'relative' }}>
                  <TextArea
                    value={pasteText}
                    onChange={e => setPasteText(e.target.value)}
                    placeholder="Paste anything from the listing page..."
                    rows={12}
                    style={{ fontFamily: "'DM Sans', monospace, sans-serif", fontSize: 12 }}
                  />
                  {pasteText && (
                    <button
                      type="button"
                      onClick={() => setPasteText('')}
                      style={m.clearBtn}
                      title="Clear paste box"
                    >Clear</button>
                  )}
                </div>
              </Field>
              <div style={{ fontSize: 11, color: LIGHT, marginTop: 4 }}>
                Tip: include the address, asking price, owner phone, beds/baths/sqft. The more you paste, the more accurate the parse.
                {pasteText && <> · {pasteText.length.toLocaleString()} chars pasted</>}
              </div>
            </>
          )}

          {step === 'review' && (
            <ReviewForm data={data} setData={setData} />
          )}

          {dupes.length > 0 && (
            <DuplicateLeadAlert
              dupes={dupes}
              onContinue={() => { setProceedAnyway(true); setDupes([]); }}
              onCancel={() => { setDupes([]); }}
            />
          )}

          {introSmsRequest && (
            <IntroSmsConfirm
              phone={introSmsRequest.phone}
              ownerName={introSmsRequest.ownerName}
              city={introSmsRequest.city}
              state={introSmsRequest.state}
              assetType={introSmsRequest.assetType}
              agentName={agentFirstName(profile)}
              onSend={handleIntroSend}
              onSkip={handleIntroSkip}
            />
          )}

          {optOut && (
            <OptOutWarningModal
              optOut={optOut}
              isAdmin={profile?.role === 'admin'}
              onCancel={() => setOptOut(null)}
              onOverride={async (confirmation) => {
                // Admin must call the Cloud Function to actually clear
                // the opt-out (admin SDK writes); then we continue the save.
                try {
                  const fn = httpsCallable(functions, 'clearOptOut');
                  await fn({ phone: optOut.phone, confirmation });
                  setOptOutOverridden(true);
                  setOptOut(null);
                } catch (e) {
                  setErr(`Couldn't clear opt-out: ${e.message}`);
                }
              }}
            />
          )}

          {err && <div style={m.err}>{err}</div>}
          {savedFlash && <div style={m.success}>{savedFlash}</div>}

          <div style={m.actions}>
            {step === 'mode' && (
              <Btn type="button" variant="ghost" onClick={handleClose}>Cancel</Btn>
            )}
            {step === 'manual' && (
              <>
                <Btn type="button" variant="ghost" onClick={() => setStep('mode')}>← Back</Btn>
                <span style={{ flex: 1 }} />
                <Btn type="button" variant="ghost" onClick={handleSaveAndAddAnother} disabled={saving}>{saving ? 'Saving…' : '+ Add Another Lead'}</Btn>
                <Btn type="button" onClick={handleSaveAndClose} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
                <Btn type="button" variant="gold" onClick={handleOpenProfile} disabled={saving}>{saving ? 'Saving…' : 'Open Client Profile →'}</Btn>
              </>
            )}
            {step === 'parse-input' && (
              <>
                <Btn type="button" variant="ghost" onClick={() => setStep('mode')}>← Back</Btn>
                <span style={{ flex: 1 }} />
                <Btn type="button" variant="gold" onClick={runParse} disabled={parsing}>
                  {parsing ? 'Parsing listing…' : '✨ Parse and review →'}
                </Btn>
              </>
            )}
            {step === 'review' && (
              <>
                <Btn type="button" variant="ghost" onClick={() => setStep('parse-input')}>← Back to paste</Btn>
                {savedLeadId && pasteText && (
                  <Btn type="button" variant="ghost" onClick={reparse} disabled={parsing || saving} title="Re-run AI parse on the saved text">
                    {parsing ? 'Re-parsing…' : '🪄 Re-parse'}
                  </Btn>
                )}
                <span style={{ flex: 1 }} />
                <Btn type="button" variant="ghost" onClick={handleSaveAndAddAnother} disabled={saving || parsing}>{saving ? 'Saving…' : '+ Add Another'}</Btn>
                <Btn type="button" onClick={handleSaveAndClose} disabled={saving || parsing}>{saving ? 'Saving…' : (savedLeadId ? 'Done' : 'Save')}</Btn>
                <Btn type="button" variant="gold" onClick={handleOpenProfile} disabled={saving || parsing}>{saving ? 'Saving…' : 'Open Client Profile →'}</Btn>
              </>
            )}
          </div>
        </div>
      </div>

      {showDiscardConfirm && (
        <div style={discardS.backdrop} onClick={() => setShowDiscardConfirm(false)}>
          <div style={discardS.panel} onClick={e => e.stopPropagation()}>
            <div style={discardS.title}>Discard your changes?</div>
            <div style={discardS.body}>You have unsaved edits on this lead. Closing now will throw them away.</div>
            <div style={discardS.actions}>
              <Btn variant="ghost" onClick={() => setShowDiscardConfirm(false)}>Keep editing</Btn>
              <Btn variant="danger" onClick={confirmDiscard}>Discard & close</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const discardS = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.65)', zIndex: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  panel: { background: '#fff', borderRadius: 14, maxWidth: 420, width: '100%', padding: '24px 26px', boxShadow: '0 24px 64px rgba(0,0,0,0.4)', fontFamily: "'DM Sans', sans-serif" },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: BURG_DK, marginBottom: 10 },
  body: { fontSize: 13, color: MID, lineHeight: 1.6, marginBottom: 18 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10 }
};

// ─────────────────────────────────────────────────────────────
// Lead-data shape, builders, and form sub-components
// ─────────────────────────────────────────────────────────────
function blankLeadData() {
  return {
    // 'property' (a house/structure) or 'land' (vacant land). Drives the
    // workflow — labels, the intro-SMS default, and which contract variant
    // gets generated.
    assetType: 'property',
    address: { line1: '', city: '', state: '', zip: '' },
    owners: [{ name: '', phone: '', email: '' }],
    listingUrl: '',
    askingPrice: '',
    beds: '',
    baths: '',
    sqft: '',
    listing: { source: '', type: '', daysOnMarket: '', listedDate: '', views: '', saves: '', listedBy: '', description: '', ownerLove: '' },
    propertyData: { propertyType: '', yearBuilt: '', lotSize: '', lotSizeUnit: '', architecturalStyle: '', materials: '', foundation: '', newConstruction: null, exteriorFeatures: '', fullBathrooms: '', halfBathrooms: '', heating: '', cooling: '', parcelNumber: '', pricePerSqft: '', zestimate: '', hoaFees: '' },
    financial: { taxAssessedValue: '', annualTaxAmount: '', rentZestimate: '', estimatedMonthlyPayment: '', ownerEstimatedRentLow: '', ownerEstimatedRentHigh: '', ownerFinancingOffered: false, ownerFinancingTerms: '', downPaymentAssistanceAvailable: false },
    saleHistory: [],
    taxHistory: [],
    climate: { floodZone: '' },
    neighborhood: { name: '', walkScore: '', transitScore: '', bikeScore: '' },
    schools: []
  };
}

function hasContent(d) {
  return !!(d.address?.line1 || d.owners?.[0]?.name || d.owners?.[0]?.phone || d.listingUrl || d.askingPrice);
}

// Strip empty strings → null, normalize numbers, drop empty owners
function buildLeadDoc(d) {
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const str = (v) => (v == null ? '' : String(v));
  return {
    assetType: d.assetType === 'land' ? 'land' : 'property',
    address: {
      line1: str(d.address?.line1).trim(),
      city: str(d.address?.city).trim(),
      state: str(d.address?.state).trim(),
      zip: str(d.address?.zip).trim()
    },
    owners: (d.owners || [])
      .filter(o => o.name?.trim() || o.phone?.trim() || o.email?.trim())
      .map(o => ({ name: str(o.name).trim(), phone: str(o.phone).trim(), email: str(o.email).trim() })),
    listingUrl: str(d.listingUrl).trim(),
    propertyData: {
      askingPrice: num(d.askingPrice),
      beds: num(d.beds),
      baths: num(d.baths),
      sqft: num(d.sqft),
      propertyType: str(d.propertyData?.propertyType),
      yearBuilt: num(d.propertyData?.yearBuilt),
      lotSize: num(d.propertyData?.lotSize),
      lotSizeUnit: str(d.propertyData?.lotSizeUnit) || 'sqft',
      architecturalStyle: str(d.propertyData?.architecturalStyle),
      materials: str(d.propertyData?.materials),
      foundation: str(d.propertyData?.foundation),
      newConstruction: d.propertyData?.newConstruction ?? null,
      exteriorFeatures: str(d.propertyData?.exteriorFeatures),
      fullBathrooms: num(d.propertyData?.fullBathrooms),
      halfBathrooms: num(d.propertyData?.halfBathrooms),
      heating: str(d.propertyData?.heating),
      cooling: str(d.propertyData?.cooling),
      parcelNumber: str(d.propertyData?.parcelNumber),
      pricePerSqft: num(d.propertyData?.pricePerSqft),
      zestimate: num(d.propertyData?.zestimate),
      hoaFees: num(d.propertyData?.hoaFees)
    },
    listing: {
      source: str(d.listing?.source),
      type: str(d.listing?.type),
      daysOnMarket: num(d.listing?.daysOnMarket),
      listedDate: str(d.listing?.listedDate),
      views: num(d.listing?.views),
      saves: num(d.listing?.saves),
      listedBy: str(d.listing?.listedBy),
      description: str(d.listing?.description),
      ownerLove: str(d.listing?.ownerLove)
    },
    financial: {
      taxAssessedValue: num(d.financial?.taxAssessedValue),
      annualTaxAmount: num(d.financial?.annualTaxAmount),
      rentZestimate: num(d.financial?.rentZestimate),
      estimatedMonthlyPayment: num(d.financial?.estimatedMonthlyPayment),
      ownerEstimatedRentLow: num(d.financial?.ownerEstimatedRentLow),
      ownerEstimatedRentHigh: num(d.financial?.ownerEstimatedRentHigh),
      ownerFinancingOffered: !!d.financial?.ownerFinancingOffered,
      ownerFinancingTerms: str(d.financial?.ownerFinancingTerms),
      downPaymentAssistanceAvailable: !!d.financial?.downPaymentAssistanceAvailable
    },
    saleHistory: Array.isArray(d.saleHistory) ? d.saleHistory : [],
    taxHistory: Array.isArray(d.taxHistory) ? d.taxHistory : [],
    climate: { floodZone: str(d.climate?.floodZone) },
    neighborhood: {
      name: str(d.neighborhood?.name),
      walkScore: num(d.neighborhood?.walkScore),
      transitScore: num(d.neighborhood?.transitScore),
      bikeScore: num(d.neighborhood?.bikeScore)
    },
    schools: Array.isArray(d.schools) ? d.schools : []
  };
}

// Merge AI-parsed JSON into the working form data, preferring parsed values
// where present and keeping any user-typed values where parsed is null.
function mergeParsedIntoData(current, parsed) {
  if (!parsed) return current;
  const blank = blankLeadData();
  const pick = (a, b) => (a !== null && a !== undefined && a !== '' ? a : (b ?? ''));
  const owners = (parsed.owners && parsed.owners.length ? parsed.owners : current.owners)
    .map((o, i) => ({
      name: pick(o?.name, current.owners?.[i]?.name),
      phone: pick(formatPhoneIfNeeded(o?.phone), current.owners?.[i]?.phone),
      email: pick(o?.email, current.owners?.[i]?.email)
    }));
  return {
    ...blank,
    address: {
      line1: pick(parsed.address?.line1, current.address?.line1),
      city: pick(parsed.address?.city, current.address?.city),
      state: pick(parsed.address?.state, current.address?.state),
      zip: pick(parsed.address?.zip, current.address?.zip)
    },
    owners: owners.length ? owners : [{ name: '', phone: '', email: '' }],
    listingUrl: pick(parsed.listingUrl, current.listingUrl),
    askingPrice: pick(parsed.askingPrice, current.askingPrice),
    beds: pick(parsed.beds, current.beds),
    baths: pick(parsed.baths, current.baths),
    sqft: pick(parsed.sqft, current.sqft),
    listing: { ...blank.listing, ...(parsed.listing || {}) },
    propertyData: { ...blank.propertyData, ...(parsed.propertyData || {}) },
    financial: { ...blank.financial, ...(parsed.financial || {}) },
    saleHistory: Array.isArray(parsed.saleHistory) ? parsed.saleHistory : [],
    taxHistory: Array.isArray(parsed.taxHistory) ? parsed.taxHistory : [],
    climate: { ...blank.climate, ...(parsed.climate || {}) },
    neighborhood: { ...blank.neighborhood, ...(parsed.neighborhood || {}) },
    schools: Array.isArray(parsed.schools) ? parsed.schools : []
  };
}
function formatPhoneIfNeeded(p) {
  if (!p) return p;
  if (/\(\d{3}\) \d{3}-\d{4}/.test(p)) return p;
  return formatPhone(p);
}

// ─── Manual form (minimal: name/phone/url/address) ───────────
// Segmented Property / Land selector. Drives labels, the intro-SMS default,
// and which contract variant gets generated downstream.
function AssetTypeToggle({ value, onChange }) {
  const opts = [
    { id: 'property', label: '🏠 Property', sub: 'House / structure' },
    { id: 'land', label: '🌳 Land', sub: 'Vacant land' }
  ];
  const v = value === 'land' ? 'land' : 'property';
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      {opts.map(o => {
        const active = v === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            style={{
              flex: 1, textAlign: 'left', cursor: 'pointer', padding: '12px 14px',
              borderRadius: 8, fontFamily: "'DM Sans', sans-serif",
              border: `1px solid ${active ? BURG : BORDER}`,
              background: active ? GOLD_PALE : '#fff',
              borderLeft: `3px solid ${active ? GOLD : BORDER}`
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: active ? BURG_DK : MID }}>{o.label}</div>
            <div style={{ fontSize: 11.5, color: LIGHT, marginTop: 2 }}>{o.sub}</div>
          </button>
        );
      })}
    </div>
  );
}

function ManualForm({ data, setData }) {
  const setOwner = (i, k, v) => setData(d => ({
    ...d, owners: d.owners.map((o, idx) => idx === i ? { ...o, [k]: v } : o)
  }));
  const setAddr = (k, v) => setData(d => ({ ...d, address: { ...d.address, [k]: v } }));
  const o = data.owners[0] || {};

  return (
    <>
      <div style={m.section}>What's for sale?</div>
      <AssetTypeToggle value={data.assetType} onChange={(v) => setData(d => ({ ...d, assetType: v }))} />

      <div style={{ ...m.section, marginTop: 14 }}>Owner</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Owner name" required>
          <Input autoFocus value={o.name} onChange={e => setOwner(0, 'name', e.target.value)} placeholder="Jane Doe" />
        </Field>
        <Field label="Phone" required>
          <Input value={o.phone} onChange={e => setOwner(0, 'phone', formatPhone(e.target.value))} placeholder="(555) 123-4567" inputMode="tel" />
        </Field>
      </div>
      <Field label="Email" hint="Optional. Required later if you send contracts to this owner.">
        <Input type="email" value={o.email} onChange={e => setOwner(0, 'email', e.target.value)} placeholder="jane@example.com" />
      </Field>

      <div style={{ ...m.section, marginTop: 14 }}>Property</div>
      <Field label="Listing website URL" required>
        <Input type="url" value={data.listingUrl} onChange={e => setData(d => ({ ...d, listingUrl: e.target.value }))} placeholder="https://www.zillow.com/homedetails/..." />
      </Field>
      <Field label="Street address" required hint="Start typing — Google will fill city/state/zip.">
        <AddressAutocomplete
          value={data.address.line1}
          onChange={e => setAddr('line1', e.target.value)}
          onSelect={(parts) => setData(d => ({
            ...d,
            address: {
              line1: parts.line1 || d.address.line1,
              city: parts.city || d.address.city,
              state: parts.state || d.address.state,
              zip: parts.zip || d.address.zip
            }
          }))}
          placeholder="2847 Thornwood Lane"
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
        <Field label="City"><Input value={data.address.city} onChange={e => setAddr('city', e.target.value)} /></Field>
        <Field label="State"><Input value={data.address.state} onChange={e => setAddr('state', e.target.value.toUpperCase().slice(0,2))} maxLength={2} /></Field>
        <Field label="Zip"><Input
          value={data.address.zip}
          onChange={e => {
            const v = e.target.value;
            setAddr('zip', v);
            if (/^\d{5}$/.test(v)) {
              lookupZip(v).then(r => {
                if (!r) return;
                setData(d => ({
                  ...d,
                  address: {
                    ...d.address,
                    city: d.address.city || r.city,
                    state: d.address.state || r.state
                  }
                }));
              });
            }
          }}
          maxLength={5} inputMode="numeric"
        /></Field>
      </div>
    </>
  );
}

// ─── Review form (everything visible, key fields pinned, rest in accordions) ───
function ReviewForm({ data, setData }) {
  const setOwner = (i, k, v) => setData(d => ({
    ...d, owners: d.owners.map((o, idx) => idx === i ? { ...o, [k]: v } : o)
  }));
  const setAddr = (k, v) => setData(d => ({ ...d, address: { ...d.address, [k]: v } }));
  const setKey = (k, v) => setData(d => ({ ...d, [k]: v }));
  const setNested = (group, k, v) => setData(d => ({ ...d, [group]: { ...d[group], [k]: v } }));
  const o = data.owners[0] || {};

  return (
    <>
      <div style={m.parsedFlash}>
        ✨ Parsed from listing — review the highlighted fields and edit anything that's off.
      </div>

      <div style={m.section}>What's for sale?</div>
      <AssetTypeToggle value={data.assetType} onChange={(v) => setKey('assetType', v)} />

      {/* PINNED — KEY DETAILS ALWAYS VISIBLE */}
      <div style={{ ...m.section, marginTop: 14 }}>Key details</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Owner name" required>
          <Input autoFocus value={o.name} onChange={e => setOwner(0, 'name', e.target.value)} />
        </Field>
        <Field label="Phone" required>
          <Input value={o.phone} onChange={e => setOwner(0, 'phone', formatPhone(e.target.value))} inputMode="tel" />
        </Field>
      </div>
      <Field label="Listing URL" required>
        <Input type="url" value={data.listingUrl} onChange={e => setKey('listingUrl', e.target.value)} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 12 }}>
        <Field label="Asking price ($)"><Input type="number" value={data.askingPrice} onChange={e => setKey('askingPrice', e.target.value)} /></Field>
        <Field label="Beds"><Input type="number" value={data.beds} onChange={e => setKey('beds', e.target.value)} /></Field>
        <Field label="Baths"><Input type="number" step="0.5" value={data.baths} onChange={e => setKey('baths', e.target.value)} /></Field>
        <Field label="Sq Ft"><Input type="number" value={data.sqft} onChange={e => setKey('sqft', e.target.value)} /></Field>
      </div>
      <Field label="Street address" required hint="Type to search — Google fills city/state/zip.">
        <AddressAutocomplete
          value={data.address.line1}
          onChange={e => setAddr('line1', e.target.value)}
          onSelect={(parts) => setData(d => ({
            ...d,
            address: {
              line1: parts.line1 || d.address.line1,
              city: parts.city || d.address.city,
              state: parts.state || d.address.state,
              zip: parts.zip || d.address.zip
            }
          }))}
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
        <Field label="City"><Input value={data.address.city} onChange={e => setAddr('city', e.target.value)} /></Field>
        <Field label="State"><Input value={data.address.state} onChange={e => setAddr('state', e.target.value.toUpperCase().slice(0,2))} maxLength={2} /></Field>
        <Field label="Zip"><Input value={data.address.zip} onChange={e => setAddr('zip', e.target.value)} /></Field>
      </div>
      <Field label="Email">
        <Input type="email" value={o.email} onChange={e => setOwner(0, 'email', e.target.value)} />
      </Field>

      {/* ACCORDIONS — collapsed by default */}
      <Accordion title="Property details">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Property type"><Input value={data.propertyData.propertyType} onChange={e => setNested('propertyData', 'propertyType', e.target.value)} placeholder="SingleFamily, Condo, MultiFamily…" /></Field>
          <Field label="Year built"><Input type="number" value={data.propertyData.yearBuilt} onChange={e => setNested('propertyData', 'yearBuilt', e.target.value)} /></Field>
          <Field label="Lot size"><Input type="number" value={data.propertyData.lotSize} onChange={e => setNested('propertyData', 'lotSize', e.target.value)} /></Field>
          <Field label="Lot size unit">
            <Select value={data.propertyData.lotSizeUnit || 'sqft'} onChange={e => setNested('propertyData', 'lotSizeUnit', e.target.value)}>
              <option value="sqft">sqft</option><option value="acres">acres</option>
            </Select>
          </Field>
          <Field label="Architectural style"><Input value={data.propertyData.architecturalStyle} onChange={e => setNested('propertyData', 'architecturalStyle', e.target.value)} /></Field>
          <Field label="Materials"><Input value={data.propertyData.materials} onChange={e => setNested('propertyData', 'materials', e.target.value)} /></Field>
          <Field label="Foundation"><Input value={data.propertyData.foundation} onChange={e => setNested('propertyData', 'foundation', e.target.value)} /></Field>
          <Field label="Exterior features"><Input value={data.propertyData.exteriorFeatures} onChange={e => setNested('propertyData', 'exteriorFeatures', e.target.value)} /></Field>
          <Field label="Heating"><Input value={data.propertyData.heating} onChange={e => setNested('propertyData', 'heating', e.target.value)} /></Field>
          <Field label="Cooling"><Input value={data.propertyData.cooling} onChange={e => setNested('propertyData', 'cooling', e.target.value)} /></Field>
          <Field label="Parcel number"><Input value={data.propertyData.parcelNumber} onChange={e => setNested('propertyData', 'parcelNumber', e.target.value)} /></Field>
          <Field label="Price per sqft ($)"><Input type="number" value={data.propertyData.pricePerSqft} onChange={e => setNested('propertyData', 'pricePerSqft', e.target.value)} /></Field>
          <Field label="Zestimate ($)"><Input type="number" value={data.propertyData.zestimate} onChange={e => setNested('propertyData', 'zestimate', e.target.value)} /></Field>
          <Field label="HOA fees ($/mo)"><Input type="number" value={data.propertyData.hoaFees} onChange={e => setNested('propertyData', 'hoaFees', e.target.value)} /></Field>
        </div>
      </Accordion>

      <Accordion title="Listing & marketing info">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Source"><Input value={data.listing.source} onChange={e => setNested('listing', 'source', e.target.value)} placeholder="zillow / redfin / fsbo…" /></Field>
          <Field label="Listing type"><Input value={data.listing.type} onChange={e => setNested('listing', 'type', e.target.value)} placeholder="For Sale by Owner" /></Field>
          <Field label="Days on market"><Input type="number" value={data.listing.daysOnMarket} onChange={e => setNested('listing', 'daysOnMarket', e.target.value)} /></Field>
          <Field label="Listed date"><Input type="date" value={data.listing.listedDate} onChange={e => setNested('listing', 'listedDate', e.target.value)} /></Field>
          <Field label="Views"><Input type="number" value={data.listing.views} onChange={e => setNested('listing', 'views', e.target.value)} /></Field>
          <Field label="Saves"><Input type="number" value={data.listing.saves} onChange={e => setNested('listing', 'saves', e.target.value)} /></Field>
        </div>
        <Field label="Listed by"><Input value={data.listing.listedBy} onChange={e => setNested('listing', 'listedBy', e.target.value)} /></Field>
        <Field label="Owner / listing description" hint="Verbatim from the listing — useful for cold-call talking points.">
          <TextArea rows={5} value={data.listing.description} onChange={e => setNested('listing', 'description', e.target.value)} />
        </Field>
        <Field label="What the owner loves about this home">
          <TextArea rows={2} value={data.listing.ownerLove} onChange={e => setNested('listing', 'ownerLove', e.target.value)} />
        </Field>
      </Accordion>

      <Accordion title="Financial & deal terms">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Tax assessed value ($)"><Input type="number" value={data.financial.taxAssessedValue} onChange={e => setNested('financial', 'taxAssessedValue', e.target.value)} /></Field>
          <Field label="Annual tax ($)"><Input type="number" value={data.financial.annualTaxAmount} onChange={e => setNested('financial', 'annualTaxAmount', e.target.value)} /></Field>
          <Field label="Rent Zestimate ($/mo)"><Input type="number" value={data.financial.rentZestimate} onChange={e => setNested('financial', 'rentZestimate', e.target.value)} /></Field>
          <Field label="Est. monthly payment ($)"><Input type="number" value={data.financial.estimatedMonthlyPayment} onChange={e => setNested('financial', 'estimatedMonthlyPayment', e.target.value)} /></Field>
          <Field label="Owner-estimated rent low ($)"><Input type="number" value={data.financial.ownerEstimatedRentLow} onChange={e => setNested('financial', 'ownerEstimatedRentLow', e.target.value)} /></Field>
          <Field label="Owner-estimated rent high ($)"><Input type="number" value={data.financial.ownerEstimatedRentHigh} onChange={e => setNested('financial', 'ownerEstimatedRentHigh', e.target.value)} /></Field>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: BURG_DK, marginBottom: 10 }}>
          <input type="checkbox" checked={!!data.financial.ownerFinancingOffered} onChange={e => setNested('financial', 'ownerFinancingOffered', e.target.checked)} />
          Owner financing offered
        </label>
        {data.financial.ownerFinancingOffered && (
          <Field label="Owner financing terms" hint="Verbatim from the listing.">
            <TextArea rows={3} value={data.financial.ownerFinancingTerms} onChange={e => setNested('financial', 'ownerFinancingTerms', e.target.value)} />
          </Field>
        )}
      </Accordion>

      {data.saleHistory?.length > 0 && (
        <Accordion title={`Sale & rent history (${data.saleHistory.length})`}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr><th style={accTh}>Date</th><th style={accTh}>Event</th><th style={accTh}>Price</th><th style={accTh}>Source</th></tr>
            </thead>
            <tbody>
              {data.saleHistory.map((h, i) => (
                <tr key={i}>
                  <td style={accTd}>{h.date || '—'}</td>
                  <td style={accTd}>{h.event || '—'}</td>
                  <td style={accTd}>{h.price ? `$${Number(h.price).toLocaleString()}` : '—'}</td>
                  <td style={accTd}>{h.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Accordion>
      )}

      {data.taxHistory?.length > 0 && (
        <Accordion title={`Tax history (${data.taxHistory.length})`}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr><th style={accTh}>Year</th><th style={accTh}>Tax paid</th><th style={accTh}>Assessment</th></tr>
            </thead>
            <tbody>
              {data.taxHistory.map((h, i) => (
                <tr key={i}>
                  <td style={accTd}>{h.year || '—'}</td>
                  <td style={accTd}>{h.taxAmount ? `$${Number(h.taxAmount).toLocaleString()}` : '—'}</td>
                  <td style={accTd}>{h.assessment ? `$${Number(h.assessment).toLocaleString()}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Accordion>
      )}

      <Accordion title="Neighborhood, schools & climate">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          <Field label="Neighborhood"><Input value={data.neighborhood.name} onChange={e => setNested('neighborhood', 'name', e.target.value)} /></Field>
          <Field label="Walk score"><Input type="number" value={data.neighborhood.walkScore} onChange={e => setNested('neighborhood', 'walkScore', e.target.value)} /></Field>
          <Field label="Transit score"><Input type="number" value={data.neighborhood.transitScore} onChange={e => setNested('neighborhood', 'transitScore', e.target.value)} /></Field>
          <Field label="Bike score"><Input type="number" value={data.neighborhood.bikeScore} onChange={e => setNested('neighborhood', 'bikeScore', e.target.value)} /></Field>
        </div>
        <Field label="Flood zone"><Input value={data.climate.floodZone} onChange={e => setNested('climate', 'floodZone', e.target.value)} placeholder="FEMA Zone X (minimal risk)" /></Field>
        {data.schools?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
            <thead>
              <tr><th style={accTh}>School</th><th style={accTh}>Grades</th><th style={accTh}>Rating</th><th style={accTh}>Distance</th></tr>
            </thead>
            <tbody>
              {data.schools.map((s, i) => (
                <tr key={i}>
                  <td style={accTd}>{s.name}</td>
                  <td style={accTd}>{s.grades}</td>
                  <td style={accTd}>{s.rating ? `${s.rating}/10` : '—'}</td>
                  <td style={accTd}>{s.distance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Accordion>
    </>
  );
}

// ─── Accordion (state-controlled, native-looking) ────────────
function Accordion({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={m.acc}>
      <button type="button" onClick={() => setOpen(o => !o)} style={m.accHead}>
        <span style={{ ...m.accChevron, transform: open ? 'rotate(90deg)' : 'rotate(0)' }}>▸</span>
        <span>{title}</span>
      </button>
      {open && <div style={m.accBody}>{children}</div>}
    </div>
  );
}
const accTh = { textAlign: 'left', padding: '6px 8px', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}` };
const accTd = { padding: '6px 8px', borderBottom: `1px solid ${BORDER}`, color: BURG_DK };

function formatPhone(v) {
  const d = (v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

const normalizePhone = (p) => (p || '').replace(/\D/g, '').replace(/^1?/, '');

// Pulls a sensible first name out of profile.name. Skips initials like "T."
// so "T. Laketia Woodley" returns "Laketia" and "Denicia Williams" returns "Denicia".
function agentFirstName(profile) {
  const n = (profile?.name || '').trim();
  if (!n) return profile?.email?.split('@')[0] || 'your agent';
  const parts = n.split(/\s+/);
  for (const p of parts) {
    if (p.length > 1 && !p.endsWith('.')) return p;
  }
  return parts[0] || 'your agent';
}
const addressKey = (line1, zip) => `${(line1 || '').trim().toLowerCase().replace(/\s+/g, ' ')}|${(zip || '').trim()}`;

// Normalize a listing URL so two visually-identical Zillow links (one with
// www., one without; with/without query string) compare equal.
function normalizeListingUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(String(url).trim());
    return `${u.hostname.toLowerCase().replace(/^www\./, '')}${u.pathname.replace(/\/+$/, '').toLowerCase()}`;
  } catch {
    return String(url).trim().toLowerCase().replace(/[?#].*$/, '').replace(/\/+$/, '');
  }
}

// Find existing leads under this agent that match by phone, address (line1+zip),
// OR listing URL. Phone or address still wins as the strongest signal but URL
// catches the "same Zillow listing copy/pasted twice" case.
async function findDuplicateLeads(agentUid, { phone, line1, zip, listingUrl }) {
  const phoneN = normalizePhone(phone);
  const addrK = line1 && zip ? addressKey(line1, zip) : null;
  const urlK = normalizeListingUrl(listingUrl);
  if (!phoneN && !addrK && !urlK) return [];
  const snap = await getDocs(collection(db, 'agents', agentUid, 'leads'));
  const matches = [];
  for (const d of snap.docs) {
    const lead = { id: d.id, ...d.data() };
    const phones = (lead.owners || []).map(o => normalizePhone(o.phone)).filter(Boolean);
    const leadKey = addressKey(lead.address?.line1, lead.address?.zip);
    const leadUrlKey = normalizeListingUrl(lead.listingUrl);
    let matchType = null;
    if (phoneN && phones.includes(phoneN)) matchType = 'phone';
    else if (addrK && leadKey === addrK) matchType = 'address';
    else if (urlK && leadUrlKey === urlK) matchType = 'listing URL';
    if (matchType) matches.push({ ...lead, matchType });
  }
  return matches;
}

// Branded confirm popup before sending an intro SMS. The agent picks one of
// the preset messages (or writes their own), edits the text, then must tick a
// box confirming the city & state are included before Send unlocks. This is
// the ONLY intro-send path, so the seller can never get a surprise/duplicate
// text. {city}/{state} are filled in when known; otherwise they show as
// [city]/[state] placeholders the agent has to replace.
function IntroSmsConfirm({ phone, ownerName, city, state, agentName, assetType, onSend, onSkip }) {
  // Default to the message that matches the lead's type (land vs property).
  const defaultOpt = INTRO_SMS_OPTIONS.find(o => o.id === (assetType === 'land' ? 'land_for_sale' : 'property_for_sale')) || INTRO_SMS_OPTIONS[0];
  const [tplId, setTplId] = useState(defaultOpt.id);
  const [body, setBody] = useState(
    interpolateIntro(defaultOpt.body, { agentName, city, state })
  );
  const [verified, setVerified] = useState(false);

  const pick = (opt) => {
    setTplId(opt.id);
    setBody(opt.id === 'custom' ? '' : interpolateIntro(opt.body, { agentName, city, state }));
    setVerified(false);
  };

  const missingPlaceholder = /\[city\]|\[state\]/i.test(body);
  const canSend = body.trim().length > 0 && verified;

  return (
    <div style={smsS.overlay}>
      <div style={{ ...smsS.card, maxWidth: 560 }}>
        <div style={smsS.eyebrow}>📤 Send intro SMS?</div>
        <h3 style={smsS.title}>Text <em style={{ fontStyle: 'italic', color: BURG }}>{ownerName}</em> at <span style={smsS.phone}>{phone}</span>?</h3>

        <div style={smsS.previewLabel}>Choose a message</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {INTRO_SMS_OPTIONS.map(opt => (
            <button key={opt.id} type="button" onClick={() => pick(opt)}
              style={{ ...smsS.tplBtn, ...(tplId === opt.id ? smsS.tplBtnActive : {}) }}>
              {opt.label}
            </button>
          ))}
          <button type="button" onClick={() => pick({ id: 'custom' })}
            style={{ ...smsS.tplBtn, ...(tplId === 'custom' ? smsS.tplBtnActive : {}) }}>
            ✎ Custom — type your own
          </button>
        </div>

        <div style={smsS.previewLabel}>Message (edit before sending)</div>
        <TextArea rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="Type your message…" />
        <div style={smsS.previewMeta}>{body.length} chars · {body.length <= 160 ? '1 segment' : Math.ceil(body.length / 153) + ' segments'}</div>

        {missingPlaceholder && (
          <div style={smsS.warn}>
            Replace <code>[city]</code> / <code>[state]</code> with the real city and state before sending.
          </div>
        )}

        <label style={smsS.verifyRow}>
          <input type="checkbox" checked={verified} onChange={e => setVerified(e.target.checked)} style={{ width: 17, height: 17, marginTop: 1, accentColor: BURG }} />
          <span>I've confirmed the <strong style={{ fontWeight: 600 }}>city and state</strong> are included and correct in this message.</span>
        </label>

        <div style={smsS.actions}>
          <Btn type="button" variant="ghost" onClick={onSkip}>Skip for now</Btn>
          <Btn type="button" variant="gold" onClick={() => onSend(body.trim())} disabled={!canSend}>📤 Send intro SMS</Btn>
        </div>
        <div style={smsS.foot}>
          Nothing is sent until you press <em>Send</em> — and it only sends once. The text goes out from your business SMS number.
        </div>
      </div>
    </div>
  );
}

const smsS = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: { background: '#fff', borderRadius: 10, maxWidth: 520, width: '100%', padding: '28px 30px', boxShadow: '0 24px 64px rgba(0,0,0,0.4)', border: `1px solid ${GOLD}` },
  eyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 8 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 500, color: BURG_DK, lineHeight: 1.2, margin: '0 0 18px', letterSpacing: '-0.3px' },
  phone: { fontFamily: 'monospace', fontSize: 17, color: BURG_DK, fontStyle: 'normal' },
  preview: { background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '14px 16px', marginBottom: 18 },
  previewLabel: { fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: LIGHT, marginBottom: 6, fontWeight: 500 },
  previewBody: { fontSize: 14, color: '#1C1C1C', lineHeight: 1.55, whiteSpace: 'pre-wrap' },
  previewMeta: { fontSize: 11, color: LIGHT, marginTop: 8 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  foot: { fontSize: 11, color: LIGHT, lineHeight: 1.5, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${BORDER}` },
  tplBtn: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '6px 12px', fontSize: 12, color: MID, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  tplBtnActive: { background: BURG, color: '#fff', borderColor: BURG },
  warn: { fontSize: 12, color: '#991B1B', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, padding: '8px 10px', marginTop: 8 },
  verifyRow: { display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: BURG_DK, margin: '14px 0 2px', cursor: 'pointer', lineHeight: 1.45 }
};

// Phone-first new-lead flow. Just one field (phone), one button. On
// "Save and continue" we create the lead and hand off to the shared
// IntroSmsConfirm popup (pick a template, edit, verify city/state, send),
// then land on the lead's page.
function PhoneFirstStep({ onCancel, onSubmit, saving }) {
  const [phone, setPhone] = useState('');

  const fmt = (v) => {
    const d = (v || '').replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };
  const valid = phone.replace(/\D/g, '').length === 10;

  return (
    <>
      <p style={{ fontSize: 13, color: '#5A5A5A', margin: '0 0 14px', lineHeight: 1.65 }}>
        Type the seller's phone number. We'll create the lead, then ask if you want to send your
        intro SMS — you'll pick a message, edit it, and confirm the city &amp; state before anything
        goes out. After that you'll land on the lead's page to fill in the rest.
      </p>
      <Field label="Seller's phone" required>
        <Input
          autoFocus
          value={phone}
          onChange={e => setPhone(fmt(e.target.value))}
          placeholder="(555) 123-4567"
          inputMode="tel"
        />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
        <Btn type="button" variant="ghost" onClick={onCancel}>← Back</Btn>
        <Btn type="button" variant="gold" onClick={() => onSubmit({ phone })} disabled={!valid || saving}>
          {saving ? 'Saving…' : 'Save and continue →'}
        </Btn>
      </div>
    </>
  );
}

// Inline duplicate alert (rendered above the action bar in the modal).
function DuplicateLeadAlert({ dupes, onContinue, onCancel }) {
  return (
    <div style={dupS.box}>
      <div style={dupS.head}>
        <span style={dupS.icon}>!</span>
        <div>
          <div style={dupS.title}>Possible duplicate{dupes.length > 1 ? 's' : ''} found</div>
          <div style={dupS.sub}>
            We already have {dupes.length === 1 ? 'a lead' : `${dupes.length} leads`} matching this {dupes[0].matchType}.
            Review the existing record before continuing — your data here is preserved.
          </div>
        </div>
      </div>
      <ul style={dupS.list}>
        {dupes.map(d => (
          <li key={d.id} style={dupS.row}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={dupS.matchTag}>matched on {d.matchType}</div>
              <div style={dupS.addr}>{d.address?.line1 || '(no address)'}{d.address?.city ? `, ${d.address.city}` : ''}{d.address?.state ? ` ${d.address.state}` : ''}{d.address?.zip ? ` ${d.address.zip}` : ''}</div>
              <div style={dupS.meta}>
                {(d.owners || []).map(o => o.name).filter(Boolean).join(', ') || '—'}
                {' · '}
                <span style={{ fontFamily: 'monospace' }}>{(d.owners || [])[0]?.phone || '—'}</span>
                {' · '}
                <em style={{ fontStyle: 'italic' }}>{d.status || '—'}</em>
              </div>
            </div>
            <a
              href={`/agent/leads/${d.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={dupS.reviewBtn}
            >Review (new tab) →</a>
          </li>
        ))}
      </ul>
      <div style={dupS.actions}>
        <Btn type="button" variant="ghost" onClick={onCancel}>Go back to editing</Btn>
        <Btn type="button" variant="gold" onClick={onContinue}>Continue anyway — create new lead</Btn>
      </div>
    </div>
  );
}

const dupS = {
  box: { background: '#FFF8E6', border: '1px solid #C9A84C', borderLeft: '3px solid #C9A84C', borderRadius: '0 6px 6px 0', padding: '14px 16px', marginTop: 14 },
  head: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  icon: { width: 22, height: 22, borderRadius: '50%', background: '#C9A84C', color: '#4A1220', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 500, color: '#4A1220', lineHeight: 1.2 },
  sub: { fontSize: 12, color: '#5A5A5A', marginTop: 4, lineHeight: 1.5 },
  list: { listStyle: 'none', margin: '8px 0', padding: 0 },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#fff', border: '1px solid rgba(107,30,46,0.1)', borderRadius: 4, marginBottom: 6 },
  matchTag: { fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A84C', marginBottom: 2 },
  addr: { fontSize: 13, fontWeight: 500, color: '#4A1220' },
  meta: { fontSize: 11, color: '#9A9A9A', marginTop: 2 },
  reviewBtn: { fontSize: 11, color: '#6B1E2E', textDecoration: 'none', padding: '7px 12px', border: '1px solid rgba(107,30,46,0.2)', borderRadius: 4, fontWeight: 500, whiteSpace: 'nowrap', fontFamily: "'DM Sans', sans-serif" },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }
};

function relativeTime(ts) {
  if (!ts?.toDate) return '';
  const diff = Date.now() - ts.toDate().getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const s = {
  toolbar: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },
  filterRow: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  filterBtn: { background: 'transparent', borderWidth: 1, borderStyle: 'solid', borderColor: BORDER, borderRadius: 999, padding: '5px 12px', fontSize: 11, color: MID, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  filterBtnActive: { background: BURG, color: '#fff', borderColor: BURG },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 18px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}` },
  tr: { cursor: 'pointer', transition: 'background 0.15s ease' },
  td: { padding: '14px 18px', fontSize: 13, borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C', verticalAlign: 'top' }
};

// Mobile card styles — Leads table swaps to this stacked-card layout
// under 900px so the columns don't squish or scroll horizontally.
const mc = {
  list: { listStyle: 'none', padding: 0, margin: 0 },
  card: { padding: '14px 16px', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 },
  row1: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  address: { fontSize: 16, fontWeight: 600, color: BURG_DK, lineHeight: 1.3, flex: 1, minWidth: 0 },
  city: { fontSize: 12, color: LIGHT, lineHeight: 1.4 },
  row2: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 4 },
  owner: { fontSize: 14, color: '#1C1C1C', fontWeight: 500 },
  phone: { fontSize: 13, color: BURG_DK, fontFamily: 'monospace' },
  metaRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' },
  fromSms: { fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: BURG, background: 'rgba(201,168,76,0.18)', border: '1px solid rgba(201,168,76,0.4)', padding: '2px 8px', borderRadius: 999 },
  assigned: { fontSize: 11, color: '#0F6B3F', fontWeight: 500 },
  open: { fontSize: 10, color: BURG, background: 'rgba(201,168,76,0.18)', border: '1px solid rgba(201,168,76,0.4)', padding: '2px 8px', borderRadius: 999, fontWeight: 500, letterSpacing: '0.04em' }
};

const m = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '60px 20px' },
  modal: { background: '#fff', borderRadius: 8, maxWidth: 680, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  head: { padding: '24px 24px 14px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  eyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: BURG_DK, margin: 0, letterSpacing: '-0.3px' },
  x: { background: 'transparent', border: 'none', fontSize: 28, color: LIGHT, cursor: 'pointer', lineHeight: 1, padding: 0 },
  section: { fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: BURG, paddingTop: 18, paddingBottom: 10, borderBottom: `1px solid ${BORDER}`, marginBottom: 14 },
  removeBtn: { width: 32, height: 38, background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 4, cursor: 'pointer', color: LIGHT, fontSize: 16, marginBottom: 14 },
  addOwner: { background: 'transparent', border: 'none', color: BURG, fontSize: 12, padding: '4px 0', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", marginBottom: 8 },
  err: { padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 12, marginTop: 10 },
  success: { padding: '10px 14px', background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.25)', borderRadius: 4, color: '#0F6B3F', fontSize: 12, marginTop: 10 },
  actions: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, paddingTop: 18, borderTop: `1px solid ${BORDER}`, flexWrap: 'wrap' },
  modeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: '12px 0 6px' },
  modeCard: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '24px 20px 22px', textAlign: 'left', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s ease', display: 'flex', flexDirection: 'column', gap: 8 },
  modeCardGold: { borderLeft: `3px solid ${GOLD}`, background: GOLD_PALE },
  modeIcon: { fontFamily: 'serif', fontSize: 22, color: BURG, lineHeight: 1 },
  modeName: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500, color: BURG_DK, marginTop: 4 },
  modeDesc: { fontSize: 12, color: MID, lineHeight: 1.55 },
  parsedFlash: { background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, borderLeft: `3px solid ${GOLD}`, color: BURG_DK, padding: '10px 14px', borderRadius: '0 4px 4px 0', fontSize: 12, marginBottom: 16 },
  clearBtn: { position: 'absolute', top: 8, right: 8, background: '#fff', border: `1px solid ${BORDER}`, color: BURG, fontSize: 10, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.04em', opacity: 0.85 },
  acc: { border: `1px solid ${BORDER}`, borderRadius: 6, marginTop: 10, overflow: 'hidden' },
  accHead: { width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: CREAM, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: BURG_DK, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' },
  accChevron: { display: 'inline-block', color: GOLD, fontSize: 12, transition: 'transform 0.15s ease', width: 12 },
  accBody: { padding: '14px 14px 4px', borderTop: `1px solid ${BORDER}`, background: '#fff' }
};
